import { ROLES } from "../../../../config/permission-maps";
import { getUserRolesInLocal } from "../../../../utils/cacheStorage";
import { VERSION_HISTORY_COPY } from "../constants/versionHistory.constants";
import { normalizeVersionName } from "../../mapping-version/utils/versionName.utils";
import {
  VersionHistoryAccess,
  VersionHistoryAccessResult,
  VersionHistoryGroup,
  VersionHistoryRecord
} from "../types/versionHistory.types";

/**
 * Reproduces ProductSummaryHistory.tsx's gating order exactly: average product first, then
 * supplier role. Called before any query fires so a gated user makes zero requests.
 */
export const resolveVersionHistoryAccess = (params: {
  isAverageProduct: boolean;
}): VersionHistoryAccessResult => {
  if (params.isAverageProduct) {
    return { access: VersionHistoryAccess.AverageProduct, isAllowed: false, message: VERSION_HISTORY_COPY.averageProductMessage };
  }

  const userRoles = getUserRolesInLocal();
  const isSupplier = userRoles?.[0] === ROLES.supplier;
  if (isSupplier) {
    return { access: VersionHistoryAccess.SupplierRole, isAllowed: false, message: VERSION_HISTORY_COPY.supplierMessage };
  }

  return { access: VersionHistoryAccess.Allowed, isAllowed: true, message: null };
};

/**
 * Returns only the fields that actually changed, or `null` when nothing did -- so a rename
 * commit with an unchanged (or only re-typed identical) name is a no-op instead of a request.
 */
export const buildVersionMetadataPayload = (params: {
  versionId: string;
  currentName: string | null;
  draftName: string;
}): { versionId: string; versionName: string } | null => {
  const normalized = normalizeVersionName(params.draftName);
  if (!normalized || normalized === (params.currentName ?? "")) {
    return null;
  }
  return { versionId: params.versionId, versionName: normalized };
};

/**
 * `null` keeps the drawing-changes query disabled -- an incomplete window is not an error state.
 * Keyed by the selected RECORD's own window, not its version's -- a version can hold several
 * calculations, and diffing all of them against the version's own creation time would compare
 * against edits that happened before most of them even ran.
 */
export const resolveDrawingChangesQueryInput = (
  record: VersionHistoryRecord | null,
  ownerId: string | undefined,
  sourceId: string | undefined
): { from: string | null; to: string | null; sourceId: string; ownerId: string } | null => {
  if (!record || !ownerId || !sourceId || !record.changedTime?.to) {
    return null;
  }
  return { from: record.changedTime.from, to: record.changedTime.to, sourceId, ownerId };
};

export const resolveTotalPages = (total: number, pageSize: number): number =>
  pageSize > 0 ? Math.ceil(total / pageSize) : 0;

export const selectDefaultRecordId = (version: VersionHistoryGroup | undefined): string | undefined =>
  version?.records[0]?.id;

export const resolveShowingRange = (page: number, pageSize: number, total: number): { from: number; to: number } => {
  if (total === 0) return { from: 0, to: 0 };
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return { from, to };
};
